import { CommonUtilService } from './CommonUtil.service';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { List } from '../../core/models/list.model';
import { BaseApiService, ListApiService } from './api';
import { ListItemDto, ListPayloadDto, ListResponseDto } from '../dto';
import { map, take, tap } from 'rxjs/operators';
import { GET_LIST_LOADING_TAG } from '../constants';
import { LoadingService } from './loading.service';
import { ListTypes } from 'src/app/core/models/savedsearches-lists.model';

export const MAX_LIST_RAW_DATA: number = 250;
@Injectable({
    providedIn: 'root',
})
export class Lists {
    listRowData: ListItemDto[] = [];
    private readonly listSubject: BehaviorSubject<ListItemDto[]> = new BehaviorSubject([]);
    private url: string = 'service/contactcloud/list';

    listPayload:ListPayloadDto = {
        page: 1,
        sortBy: 'Name',
        sortDirection: 'asc',
        listTypes: Object.keys(ListTypes),
        isPublic: true
    };
    listLength: number = 0;
    listPageSize: number = 0;

    readonly listData$: Observable<ListItemDto[]> = this.listSubject.asObservable();

    constructor(
        private readonly listApiService: ListApiService,
        private commonUtilService: CommonUtilService,
        private readonly baseApiService: BaseApiService,
        private readonly loadingService: LoadingService
    ) {
    }

    getLists(loadMore = false): Observable<ListItemDto[]> {
        return this.getListData(this.listPayload).pipe(tap((resp: ListResponseDto) => {
            if(resp.page === 1) {
                this.listPayload = { ...this.listPayload, page: resp.page };
                this.listLength = resp.totalCount;
                this.listPageSize = resp.pageSize;
            }
        }), map(resp => {
            if (!loadMore) {
                this.setLists([]);
            }
            const currentData = this.listSubject.getValue();
            return currentData.concat(resp.items || []);
        }));
    }

    setLists(data: ListItemDto[]): void {
        this.listSubject.next(data);
    }

    createPreviewList(list: List, matchOnJobHistory?): Observable<any> {
        const fd: FormData = new FormData();
        let matchOnJobHistoryFlag = '';
        if (matchOnJobHistory === true || matchOnJobHistory === false) {
            matchOnJobHistoryFlag = '&matchOnJobHistory=' + matchOnJobHistory;
        }
        fd.append('file', list.file);
        return this.baseApiService
            .post(`${this.url}/preview`, fd, { params: { type: list.type, skipFirstRow: list.skipFirstLine, matchOnJobHistoryFlag }, showError: true });
    }

    createList(list: List, matchOnJobHistory?): Observable<any> {
        const fd: FormData = new FormData();
        fd.append('file', list.file);
        const params = {
            type: list.type,
            skipFirstRow: list.skipFirstLine,
            name: encodeURIComponent(list.name),
            isPublic: list.isPublic,
            tags: JSON.stringify(list.tags),
            ...(typeof matchOnJobHistory === 'boolean' ? { matchOnJobHistory } : {}),
            ...list.columnMapping
        }
        return this.baseApiService
            .post(`${this.url}/upload`, fd, { params, showError: true,});
    }

    updateListName(itemId, parameters): Observable<any> {
        return this.baseApiService.put(`${this.url}/${itemId}`, parameters, {showError:true});
    }

    tagCompanies(body): Observable<any> {
        return this.baseApiService.post(`${this.url}/tagCompanies`, body, { showError: true });
    }

    tagContacts(body): Observable<any> {
        return this.baseApiService.post(`${this.url}/tagContacts`, body, { showError: true });
    }

    tagSuppressions(body): Observable<any> {
        return this.baseApiService.post(`${this.url}/tagSuppressions`, body, { showError: true });
    }

    addSuppressions(body): Observable<any> {
        return this.baseApiService.post(`${this.url}/addSuppressions`, body);
    }

    refreshLists(): void {
        this.getLists().pipe(take(1)).subscribe((listItems: ListItemDto[]) => {
            this.setListRawData(listItems);
        });
    }

    getListElement(element: ListItemDto): ListItemDto {
        let name = '';
        try {
            name = decodeURIComponent(element.name);
        } catch (e) {
            name = element.name;
        }
        return { ...element, name };
    }

    getListRawData(response: ListItemDto[]) {
        return response.map((element: ListItemDto) => (this.getListElement(element)));
    }

    setListRawData(response: ListItemDto[]): void {
        let list: ListItemDto[] = this.getListRawData(response)
        this.listRowData = list;
        this.setLists(list);
    }

    setPayload(paloadObj: ListPayloadDto): void {
        this.listPayload = { ...this.listPayload, ...paloadObj };
    }

    loadNextListPage(): void {
        Object.assign(this.listPayload, { page: this.listPayload.page + 1 });
        this.getLists(true).pipe(take(1)).subscribe((listItems: ListItemDto[]) => {
            this.setListRawData(listItems);
        });
    }

    getListSubject(): Observable<ListItemDto[]> {
        return this.listSubject.asObservable();
    }

    isAllRecordLoaded(): boolean {
        return this.listLength < (this.listPayload.page * this.listPageSize);
    }

    private getListData(listPayload): Observable<ListResponseDto> {
        return this.loadingService.loadingWrapper(this.listApiService.getList(listPayload), GET_LIST_LOADING_TAG)
    }
}
